/**
 * Deck-structure picker for the new-chat screen.
 *
 * TESTING AFFORDANCE — not the v1 product. v1 ships automatic selection: the
 * generator reads the request and picks the structure itself, and the user
 * never sees this list. The picker exists so we can pin one structure and
 * verify it end to end, which automatic selection alone makes awkward — you
 * can only test the template the selector happens to choose.
 *
 * TO HIDE IT: set {@link SHOW_SLIDE_TEMPLATE_PICKER} to false. That is the
 * whole removal — nothing else imports this file, and the server route it
 * calls is read-only. Deleting the file and its one usage in
 * `chat-empty-state.tsx` removes it entirely.
 *
 * How the pin actually reaches generation: picking a template pre-fills the
 * composer with a request that names it. `create_artifact` takes an optional
 * `templateKey`, and the handler matches it against the catalog BEFORE any
 * selection call — so a named structure is honoured deterministically, with no
 * model judgement involved.
 */

import { useEffect, useState } from "react";
import { LayoutTemplate } from "lucide-react";

import { api } from "@/utils/api-client";
import { cn } from "@/lib/utils";

/**
 * Master switch. `false` hides the picker everywhere and is the intended
 * state for the v1 release, where selection is fully automatic.
 *
 * Currently OFF: v1 ships automatic selection. Flip to `true` to pin a
 * specific structure while testing, then flip it back before shipping.
 */
export const SHOW_SLIDE_TEMPLATE_PICKER = false;

interface SlideTemplateSummary {
  key: string;
  name: string;
  description: string;
}

interface SlideTemplatePickerProps {
  /** Sends the composed prompt, exactly as a sample-prompt click would. */
  onPick: (prompt: string) => void;
  className?: string;
}

/**
 * Builds the prompt a pick sends.
 *
 * Phrased as a normal request that happens to name the structure, so the run
 * exercises the same path a user's own wording would. The explicit name is
 * what the handler matches on.
 */
function promptForTemplate(t: SlideTemplateSummary): string {
  return `Create a presentation using the "${t.key}" deck structure. Use realistic placeholder content so I can review the layout and structure.`;
}

export function SlideTemplatePicker({ onPick, className }: SlideTemplatePickerProps) {
  const [templates, setTemplates] = useState<SlideTemplateSummary[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api
      .get("/slide-templates", { showErrorToast: false })
      .then((res) => {
        if (cancelled) return;
        setTemplates(res?.data?.templates ?? []);
      })
      .catch(() => {
        // A missing catalog (unseeded environment) is not an error worth
        // showing — the picker simply has nothing to offer and stays hidden.
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Render nothing until we know there is something to show: an empty box
  // that appears and vanishes reads as a glitch.
  if (!loaded || templates.length === 0) return null;

  return (
    <div className={cn("w-full", className)}>
      <div className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
        <LayoutTemplate className="size-3.5" />
        <span>Deck structures</span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide">
          testing
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {templates.map((t) => (
          <button
            key={t.key}
            type="button"
            title={t.description}
            onClick={() => onPick(promptForTemplate(t))}
            className="rounded-full border border-border bg-background px-3 py-1.5 text-xs text-foreground transition-colors hover:border-primary hover:bg-primary/5"
          >
            {t.name}
          </button>
        ))}
      </div>
    </div>
  );
}
